/**
 * SaveDialog — save file dialog with file name input using FS Sidecar.
 *
 * Usage:
 *   <SaveDialog
 *     open={isOpen}
 *     title="Save File"
 *     defaultName="untitled.md"
 *     accept={[".md"]}
 *     onSave={(path) => handleSave(path)}
 *     onCancel={() => setOpen(false)}
 *   />
 */

import type { WsFsEntry } from "@magic-md/engine-client/protocol";
import { useT } from "@magic-md/i18n/react";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { workspaceFs } from "../../lib/engine-fs";
import { DialogButton, DialogOverlay } from "./DialogOverlay";
import { FileBrowser } from "./FileBrowser";

export interface SaveDialogProps {
    open: boolean;
    title?: string;
    defaultName?: string;
    accept?: string[];
    onSave: (filePath: string) => void;
    onCancel: () => void;
    /** Initial directory. Defaults to cwd. */
    initialPath?: string;
}

export function SaveDialog({
    open,
    title,
    defaultName = "untitled.md",
    accept,
    onSave,
    onCancel,
    initialPath,
}: SaveDialogProps) {
    const t = useT("editor");
    const [currentPath, setCurrentPath] = useState(initialPath ?? "");
    const [fileName, setFileName] = useState(defaultName);
    const [sep, setSep] = useState("/");
    const [sepResolved, setSepResolved] = useState(false);
    const fileNameRef = useRef<HTMLInputElement>(null);

    const [resolving, setResolving] = useState(false);

    // Resolve initial path and separator
    useEffect(() => {
        if (!open) return;
        setFileName(defaultName);
        setSepResolved(false);
        workspaceFs
            .pathInfo()
            .then((info) => {
                setSep(info.sep);
                setSepResolved(true);
            })
            .catch(() => setSepResolved(true)); // fall back to default "/"
        if (initialPath) {
            setCurrentPath(initialPath);
        } else {
            setResolving(true);
            workspaceFs
                .defaultDir()
                .then(setCurrentPath)
                .finally(() => setResolving(false));
        }
    }, [open, initialPath, defaultName]);

    // Focus and select the name part (before extension) only when dialog opens.
    // Must NOT depend on fileName — otherwise every keystroke resets the selection.
    const prevOpenRef = useRef(false);
    useEffect(() => {
        if (open && !prevOpenRef.current && fileNameRef.current) {
            const input = fileNameRef.current;
            requestAnimationFrame(() => {
                input.focus();
                const dot = input.value.lastIndexOf(".");
                if (dot > 0) {
                    input.setSelectionRange(0, dot);
                } else {
                    input.select();
                }
            });
        }
        prevOpenRef.current = open;
    }, [open]);

    const handleSelect = useCallback((entry: WsFsEntry) => {
        if (!entry.isDirectory) {
            setFileName(entry.name);
        }
    }, []);

    const handleNavigate = useCallback((path: string) => {
        setCurrentPath(path);
    }, []);

    const handleSave = () => {
        if (!fileName.trim()) return;
        const fullPath = `${currentPath}${currentPath.endsWith(sep) ? "" : sep}${fileName.trim()}`;
        onSave(fullPath);
    };

    return (
        <DialogOverlay
            title={title ?? t("dialog.saveFile")}
            open={open}
            onClose={onCancel}
            className="w-[820px]"
            footer={
                <>
                    <DialogButton onClick={onCancel}>{t("common.cancel")}</DialogButton>
                    <DialogButton
                        variant="primary"
                        onClick={handleSave}
                        disabled={!fileName.trim() || !currentPath || !sepResolved}
                    >
                        {t("common.save")}
                    </DialogButton>
                </>
            }
        >
            {resolving ? (
                <div className="h-[350px] flex items-center justify-center">
                    <Loader2 size={24} className="animate-spin text-muted-foreground" />
                </div>
            ) : !currentPath ? (
                <div className="h-[350px] flex items-center justify-center text-sm text-muted-foreground">
                    {t("dialog.noAccessibleRoots")}
                </div>
            ) : (
                <>
                    <div className="h-[380px]">
                        <FileBrowser
                            currentPath={currentPath}
                            onNavigate={handleNavigate}
                            onSelect={handleSelect}
                            mode="both"
                            accept={accept}
                            sep={sep}
                        />
                    </div>

                    {/* File name input */}
                    <div className="flex items-center gap-2 px-4 py-3 border-t border-border">
                        <label className="text-xs text-muted-foreground whitespace-nowrap">
                            {t("dialog.fileName")}
                        </label>
                        <input
                            ref={fileNameRef}
                            type="text"
                            value={fileName}
                            onChange={(e) => setFileName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleSave();
                            }}
                            className="flex-1 text-xs bg-muted/50 border border-border rounded px-2 py-1 outline-none focus:border-primary text-foreground"
                        />
                    </div>
                </>
            )}
        </DialogOverlay>
    );
}
